import dayjs from 'dayjs';
import {
  OrderReportResponse,
  OrderReportItem,
  ProductReportResponse,
  ProductReportItem,
  CustomerReportResponse,
  CustomerReportItem,
  CustomerOutstandingReportResponse,
  CustomerOutstandingReportItem,
  PaymentReportResponse,
  PaymentReportItem,
} from 'src/models/Management.interface';

export const generateOrderReportCSV = (
  data: OrderReportResponse,
  filters: {
    startDate: string | null;
    endDate: string | null;
    statuses: string[];
    customerName?: string | null;
  }
): void => {
  // Define columns
  const columns = [
    'Order #',
    'Date & Time',
    'Customer',
    'Status',
    'Grand Total',
    'Paid Amount',
    'Outstanding Amount',
  ];

  // Map rows
  const rows = data.orders.map((order: OrderReportItem) => [
    order.orderNumber,
    dayjs(order.createdAt).format('YYYY-MM-DD HH:mm'),
    order.customerName,
    order.status,
    order.grandTotal.toFixed(2),
    order.paidAmount.toFixed(2),
    (order.grandTotal - order.paidAmount).toFixed(2),
  ]);

  // Add Summary Header
  const summaryHeader = ['', '', '', '', 'Summary Statistics', '', ''];
  const summaryRows = [
    ['Total Orders', data.summary.totalOrders, '', '', '', '', ''],
    ['Total Revenue', data.summary.totalRevenue.toFixed(2), '', '', '', '', ''],
    ['Total Paid', data.summary.totalPaid.toFixed(2), '', '', '', '', ''],
    [
      'Total Outstanding',
      data.summary.totalOutstanding.toFixed(2),
      '',
      '',
      '',
      '',
      '',
    ],
  ];

  // Add Filter Info
  const filterInfo = [
    ['Filter Criteria'],
    ['Start Date', filters.startDate || 'All'],
    ['End Date', filters.endDate || 'All'],
    ['Statuses', filters.statuses.join('; ')],
    ['Customer', filters.customerName || 'All Customers'],
    [],
  ];

  // Add Products Sold Summary
  const productsSoldHeader = ['', '', 'Products Sold Summary', '', ''];
  const productsSoldColumns = [
    'Product Name',
    'Quantity Sold',
    'Total Amount',
    '',
    '',
    '',
    '',
  ];
  const productsSoldRows = (data.productsSold || []).map((product) => [
    product.productName,
    product.quantitySold,
    product.totalRevenue.toFixed(2),
    '',
    '',
    '',
    '',
  ]);

  // Combine content
  const content = [
    ...filterInfo,
    ...summaryRows,
    [],
    summaryHeader,
    columns,
    ...rows,
    [],
    [],
    productsSoldHeader,
    productsSoldColumns,
    ...productsSoldRows,
  ];

  // Convert to CSV string
  const csvString = content
    .map((row) =>
      row
        .map((field: string | number) => {
          // Escape quotes and wrap in quotes if contains commas
          const cell = String(field).replace(/"/g, '""');
          return cell.includes(',') ? `"${cell}"` : cell;
        })
        .join(',')
    )
    .join('\n');

  // Create and trigger download
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const fileName = `OrderReport-${dayjs().format('YYYYMMDD-HHmm')}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generateProductReportCSV = (
  data: ProductReportResponse,
  filters: {
    startDate: string | null;
    endDate: string | null;
  }
): void => {
  // Define columns
  const columns = ['Product Name', 'Net Production', 'Net Sales'];

  // Map rows
  const rows = data.products.map((product: ProductReportItem) => [
    product.name,
    product.netProduction,
    product.netSales,
  ]);

  // Add Summary Header
  const summaryRows = [
    ['Total Net Production', data.summary.totalProduction, ''],
    ['Total Net Sales', '', data.summary.totalSales],
  ];

  // Add Filter Info
  const filterInfo = [
    ['Filter Criteria'],
    ['Start Date', filters.startDate || 'All'],
    ['End Date', filters.endDate || 'All'],
    [],
  ];

  // Combine content
  const content = [...filterInfo, ...summaryRows, [], columns, ...rows];

  // Convert to CSV string
  const csvString = content
    .map((row) =>
      row
        .map((field: string | number | undefined | null) => {
          if (field === undefined || field === null) return '';
          // Escape quotes and wrap in quotes if contains commas
          const cell = String(field).replace(/"/g, '""');
          return cell.includes(',') ? `"${cell}"` : cell;
        })
        .join(',')
    )
    .join('\n');

  // Create and trigger download
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const fileName = `ProductReport-${dayjs().format('YYYYMMDD-HHmm')}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generateCustomerReportCSV = (
  data: CustomerReportResponse,
  filters: {
    startDate: string | null;
    endDate: string | null;
  }
): void => {
  const columns = [
    'Customer',
    'No. of Orders',
    'Grand Total',
    'Paid Amount',
    'Balance Amount',
  ];

  const rows = data.customers.map((customer: CustomerReportItem) => [
    customer.customerName,
    customer.noOfOrders,
    customer.grandTotal.toFixed(2),
    customer.paidAmount.toFixed(2),
    customer.balanceAmount.toFixed(2),
  ]);

  const summaryHeader = ['', '', 'Summary Statistics', '', ''];
  const summaryRows = [
    ['Total Orders', '', data.summary.totalOrders, '', ''],
    ['Total Revenue', '', data.summary.totalRevenue.toFixed(2), '', ''],
    ['Total Balance', '', data.summary.totalBalance.toFixed(2), '', ''],
  ];

  const filterInfo = [
    ['Filter Criteria'],
    ['Start Date', filters.startDate || 'All'],
    ['End Date', filters.endDate || 'All'],
    [],
  ];

  const content = [
    ...filterInfo,
    ...summaryRows,
    [],
    summaryHeader,
    columns,
    ...rows,
  ];

  const csvString = content
    .map((row) =>
      row
        .map((field: string | number | undefined | null) => {
          if (field === undefined || field === null) return '';
          const cell = String(field).replace(/"/g, '""');
          return cell.includes(',') ? `"${cell}"` : cell;
        })
        .join(',')
    )
    .join('\n');

  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const fileName = `CustomerReport-${dayjs().format('YYYYMMDD-HHmm')}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generateCustomerOutstandingReportCSV = (
  data: CustomerOutstandingReportResponse,
  filters: {
    customerName: string | null;
  }
): void => {
  const columns = [
    'Customer',
    'Grand Total',
    'Paid Amount',
    'Outstanding Amount',
  ];

  const rows = data.customers.map((customer: CustomerOutstandingReportItem) => [
    customer.customerName,
    customer.grandTotal.toFixed(2),
    customer.paidAmount.toFixed(2),
    customer.balanceAmount.toFixed(2),
  ]);

  const summaryHeader = ['', 'Summary Statistics', '', ''];
  const summaryRows = [
    ['Grand Total', data.summary.totalGrandTotal.toFixed(2), '', ''],
    ['Total Paid Amount', data.summary.totalPaidAmount.toFixed(2), '', ''],
    ['Total Outstanding', data.summary.totalOutstanding.toFixed(2), '', ''],
  ];

  const filterInfo = [
    ['Filter Criteria'],
    ['Customer', filters.customerName || 'All Customers'],
    [],
  ];

  const content = [
    ...filterInfo,
    ...summaryRows,
    [],
    summaryHeader,
    columns,
    ...rows,
  ];

  const csvString = content
    .map((row) =>
      row
        .map((field: string | number | undefined | null) => {
          if (field === undefined || field === null) return '';
          const cell = String(field).replace(/"/g, '""');
          return cell.includes(',') ? `"${cell}"` : cell;
        })
        .join(',')
    )
    .join('\n');

  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const fileName = `CustomerOutstandingReport-${dayjs().format(
    'YYYYMMDD-HHmm'
  )}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const generatePaymentReportCSV = (
  data: PaymentReportResponse,
  filters: {
    startDate: string | null;
    endDate: string | null;
    customerName?: string | null;
  }
): void => {
  const columns = ['Payment Date', 'Order#', 'Customer', 'Paid Amount'];

  const rows = data.payments.map((payment: PaymentReportItem) => [
    dayjs(payment.paymentDate).format('DD/MM/YYYY'),
    payment.orderNumber,
    payment.customerName,
    payment.paidAmount.toFixed(2),
  ]);

  const summaryHeader = ['', 'Summary Statistics', '', ''];
  const summaryRows = [
    ['Total Paid Amount', data.summary.totalPaidAmount.toFixed(2), '', ''],
  ];

  const filterInfo = [
    ['Filter Criteria'],
    ['Start Date', filters.startDate || 'All'],
    ['End Date', filters.endDate || 'All'],
    ['Customer', filters.customerName || 'All Customers'],
    [],
  ];

  const content = [
    ...filterInfo,
    ...summaryRows,
    [],
    summaryHeader,
    columns,
    ...rows,
  ];

  const csvString = content
    .map((row) =>
      row
        .map((field: string | number | undefined | null) => {
          if (field === undefined || field === null) return '';
          const cell = String(field).replace(/"/g, '""');
          return cell.includes(',') ? `"${cell}"` : cell;
        })
        .join(',')
    )
    .join('\n');

  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const fileName = `PaymentReport-${dayjs().format('YYYYMMDD-HHmm')}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
